import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    PanResponder,
    Platform,
    Animated,
    View
} from "react-native";
import type ICarouselProps from "./type";
import type {
    CarouselKeyboardEventType,
    CarouselPointerEventType,
    CarouselPanHandlersType,
    CarouselWebHandlersType,
    CarouselFocusEventType,
    CarouselWebPropsType,
    CarouselContextType
} from "./type";
import stylesheet, {
    CAROUSEL_SPRING_VELOCITY_SCALE,
    CAROUSEL_EDGE_RESISTANCE,
    getCarouselOverlayStyle,
    getCarouselBarStyle,
    useStyles
} from "./stylesheet";
import CarouselContent from "./components/content";
import CarouselIndicators from "./components/indicators";
import CarouselItem from "./components/item";
import CarouselNext from "./components/next";
import CarouselPause from "./components/pause";
import CarouselPrevious from "./components/previous";
import {
    CarouselContext
} from "./context";
import {
    subscribeCarouselWebEvents,
    isCarouselEditableTarget,
    getCarouselSettleIndex,
    isCarouselNodeInside,
    isCarouselTabTarget,
    clampCarouselIndex,
    getCarouselMetrics,
    getCarouselKeyStep,
    getCarouselPerView,
    getCarouselOffset
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    useReducedMotion,
    getMotionTiming,
    MOTION_SPRINGS
} from "../../helpers/motion";
import {
    usePointerType,
    useBreakpoint
} from "../../helpers/pointer";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getDirectionSign
} from "../../helpers/direction";
import {
    warnOnce
} from "../../utils/development";

const Carousel: FC<ICarouselProps> = ({
    isAutoplayEnabled: isAutoplayEnabledProp = false,
    activeIndex: activeIndexProp,
    controlsPlacement = "inside",
    orientation = "horizontal",
    "aria-label": ariaLabel,
    defaultActiveIndex = 0,
    onActiveIndexChange,
    indicator = "dots",
    isLoading = false,
    autoplayInterval,
    visibleCount = 1,
    renderThumbnail,
    size = "medium",
    customLocalize,
    isLoop = false,
    isPeek = false,
    customTheme,
    children,
    length,
    style,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        durations,
        radiuses,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isReduceMotionEnabled = useReducedMotion();

    const pointerType = usePointerType();

    const breakpoint = useBreakpoint();

    const position = useRef(new Animated.Value(0)).current;

    const animationRef = useRef<Animated.CompositeAnimation | null>(null);

    const panHandlersRef = useRef<CarouselPanHandlersType>({
        shouldSet: () => false,
        onRelease: () => {},
        onGrant: () => {},
        onMove: () => {}
    });

    const webHandlersRef = useRef<CarouselWebHandlersType>({
        onPointerEnter: () => {},
        onPointerLeave: () => {},
        onKeyDown: () => {},
        onFocus: () => {},
        onBlur: () => {}
    });

    const rootRef = useRef<View>(null);

    const targetOffsetRef = useRef<number | null>(null);

    const startOffsetRef = useRef<number>(0);

    const offsetRef = useRef<number>(0);

    const stepRef = useRef<number>(0);

    const isDraggingRef = useRef<boolean>(false);

    const [
        viewportWidth,
        setViewportWidth
    ] = useState<number>(0);

    const [
        count,
        setCount
    ] = useState<number>(0);

    const [
        indexState,
        setIndexState
    ] = useState<number>(defaultActiveIndex);

    const [
        isUserPaused,
        setIsUserPaused
    ] = useState<boolean>(false);

    const [
        isHovered,
        setIsHovered
    ] = useState<boolean>(false);

    const [
        isFocusInside,
        setIsFocusInside
    ] = useState<boolean>(false);

    const [
        isDragging,
        setIsDragging
    ] = useState<boolean>(false);

    const isVertical = orientation === "vertical";

    const gap = spaces.spacingMd;

    const currentLength = length ?? controlHeights.large * 6;

    const viewportSize = isVertical ? currentLength : viewportWidth;

    const isMeasured = viewportSize > 0;

    const perView = getCarouselPerView({
        shapeConfigs,
        viewportSize,
        visibleCount,
        orientation,
        gap
    });

    const peek = isPeek ? spaces.spacingXl + gap : 0;

    const metrics = useMemo(() => getCarouselMetrics({
        viewportSize,
        perView,
        count,
        peek,
        gap
    }), [
        viewportSize,
        perView,
        count,
        peek,
        gap
    ]);

    const pageCount = metrics.pageCount;

    const isControlled = activeIndexProp !== undefined;

    const activeIndex = clampCarouselIndex({
        index: isControlled ? activeIndexProp : indexState,
        pageCount
    });

    const visibleEnd = activeIndex + perView - 1;

    const visibleRange = useMemo(() => ({
        start: activeIndex,
        end: visibleEnd
    }), [
        activeIndex,
        visibleEnd
    ]);

    const isAutoplayEnabled = isAutoplayEnabledProp && !isReduceMotionEnabled;

    const isAutoplayPlaying = isAutoplayEnabled && !isUserPaused;

    const isFirst = !isLoop && activeIndex <= 0;

    const isLast = !isLoop && activeIndex >= pageCount - 1;

    const isPreviousDisabled = isLoading || pageCount <= 1 || isFirst;

    const isNextDisabled = isLoading || pageCount <= 1 || isLast;

    const isAutoplayRunning = isAutoplayPlaying && !isHovered && !isFocusInside && !isDragging && !isLoading && !isNextDisabled;

    const isShowControls = isLoading || pageCount > 1;

    const isCompactIndicators = breakpoint === "compact" && (indicator === "dots" || indicator === "thumbnails");

    const isEndControls = controlsPlacement === "end" && isShowControls;

    const isIndicatorInBar = (indicator === "dots" || indicator === "counter" || indicator === "thumbnails") && isShowControls;

    const goTo = useStableCallback((nextIndex: number) => {
        const target = clampCarouselIndex({
            index: nextIndex,
            pageCount
        });

        if(target === activeIndex) {
            return;
        }

        if(!isControlled) {
            setIndexState(target);
        }

        onActiveIndexChange?.(target);
    });

    const goNext = useStableCallback(() => {
        if(isNextDisabled) {
            return;
        }

        goTo(activeIndex < pageCount - 1 ? activeIndex + 1 : 0);
    });

    const goPrevious = useStableCallback(() => {
        if(isPreviousDisabled) {
            return;
        }

        goTo(activeIndex > 0 ? activeIndex - 1 : pageCount - 1);
    });

    const handleTogglePlay = useStableCallback(() => {
        if(isUserPaused) {
            setIsFocusInside(false);
        }

        setIsUserPaused((current) => !current);
    });

    const handleViewportLayout = useStableCallback((event: LayoutChangeEvent) => {
        if(isVertical) {
            return;
        }

        setViewportWidth(event.nativeEvent.layout.width);
    });

    const animateToIndex = (index: number, velocity?: number) => {
        const target = getCarouselOffset({
            metrics,
            index
        });

        targetOffsetRef.current = target;

        animationRef.current?.stop();

        const {
            duration,
            easing
        } = getMotionTiming({
            intent: "contentSwap",
            isReduceMotionEnabled,
            durations
        });

        if(!duration) {
            position.setValue(target);

            return;
        }

        if(velocity === undefined) {
            animationRef.current = Animated.timing(position, {
                useNativeDriver: false,
                toValue: target,
                duration,
                easing
            });
        } else {
            animationRef.current = Animated.spring(position, {
                ...MOTION_SPRINGS.standard,
                velocity: velocity * CAROUSEL_SPRING_VELOCITY_SCALE,
                useNativeDriver: false,
                toValue: target
            });
        }

        animationRef.current.start();
    };

    const getDragOffset = (forward: number): number => {
        const raw = startOffsetRef.current + forward;

        if(raw < 0) {
            return raw * CAROUSEL_EDGE_RESISTANCE;
        }

        if(raw > metrics.maxOffset) {
            return metrics.maxOffset + (raw - metrics.maxOffset) * CAROUSEL_EDGE_RESISTANCE;
        }

        return raw;
    };

    const getForward = ({
        horizontalDelta,
        delta
    }: {
        horizontalDelta: number;
        delta: number;
    }): number => {
        return isVertical ? -delta : -horizontalDelta * getDirectionSign({
            direction
        });
    };

    panHandlersRef.current = {
        shouldSet: (gesture) => {
            if(isLoading || pageCount <= 1) {
                return false;
            }

            const main = isVertical ? gesture.dy : gesture.dx;

            const cross = isVertical ? gesture.dx : gesture.dy;

            return Math.abs(main) > touchTargets.handle && Math.abs(main) > Math.abs(cross);
        },
        onRelease: (gesture) => {
            const offset = Math.max(0, Math.min(startOffsetRef.current + getForward({
                horizontalDelta: gesture.dx,
                delta: gesture.dy
            }), metrics.maxOffset));

            const velocity = getForward({
                horizontalDelta: gesture.vx,
                delta: gesture.vy
            });

            const settleIndex = getCarouselSettleIndex({
                coast: durations.medium,
                velocity,
                metrics,
                offset
            });

            isDraggingRef.current = false;

            setIsDragging(false);

            animateToIndex(settleIndex, velocity);

            goTo(settleIndex);
        },
        onMove: (gesture) => {
            position.setValue(getDragOffset(getForward({
                horizontalDelta: gesture.dx,
                delta: gesture.dy
            })));
        },
        onGrant: () => {
            isDraggingRef.current = true;

            animationRef.current?.stop();

            startOffsetRef.current = offsetRef.current;

            setIsDragging(true);

            if(pointerType === "coarse") {
                setIsUserPaused(true);
            }
        }
    };

    const panResponder = useRef(PanResponder.create({
        onMoveShouldSetPanResponderCapture: (_, gesture) => panHandlersRef.current.shouldSet(gesture),
        onMoveShouldSetPanResponder: (_, gesture) => panHandlersRef.current.shouldSet(gesture),
        onPanResponderTerminate: (_, gesture) => panHandlersRef.current.onRelease(gesture),
        onPanResponderRelease: (_, gesture) => panHandlersRef.current.onRelease(gesture),
        onPanResponderMove: (_, gesture) => panHandlersRef.current.onMove(gesture),
        onPanResponderGrant: () => panHandlersRef.current.onGrant(),
        onPanResponderTerminationRequest: () => false
    })).current;

    useEffect(() => {
        const listenerID = position.addListener(({
            value
        }) => {
            offsetRef.current = value;
        });

        return () => {
            position.removeListener(listenerID);

            animationRef.current?.stop();
        };
    }, []);

    useEffect(() => {
        if(isDraggingRef.current) {
            return;
        }

        const target = getCarouselOffset({
            index: activeIndex,
            metrics
        });

        if(stepRef.current !== metrics.step) {
            stepRef.current = metrics.step;

            animationRef.current?.stop();

            targetOffsetRef.current = target;

            position.setValue(target);

            return;
        }

        if(targetOffsetRef.current === target) {
            return;
        }

        animateToIndex(activeIndex);
    }, [
        metrics.maxOffset,
        metrics.step,
        activeIndex
    ]);

    useEffect(() => {
        if(!isAutoplayRunning) {
            return;
        }

        const timer = setTimeout(goNext, autoplayInterval ?? durations.autoplay);

        return () => {
            clearTimeout(timer);
        };
    }, [
        isAutoplayRunning,
        autoplayInterval,
        activeIndex
    ]);

    useEffect(() => {
        if(Platform.OS !== "web") {
            return;
        }

        return subscribeCarouselWebEvents({
            handlers: webHandlersRef,
            node: rootRef.current
        });
    }, []);

    useEffect(() => {
        if(ariaLabel || props["aria-labelledby"]) {
            return;
        }

        warnOnce({
            message: "Carousel: a carousel needs an \"aria-label\" (or \"aria-labelledby\") so screen readers can name the region."
        });
    }, [
        ariaLabel
    ]);

    const onPointerEnter = (event: CarouselPointerEventType) => {
        if(event.pointerType === "touch") {
            return;
        }

        setIsHovered(true);
    };

    const onPointerLeave = () => {
        setIsHovered(false);
    };

    const onFocus = () => {
        setIsFocusInside(true);
    };

    const onBlur = (event: CarouselFocusEventType) => {
        if(isCarouselNodeInside({
            container: event.currentTarget,
            node: event.relatedTarget
        })) {
            return;
        }

        setIsFocusInside(false);
    };

    const onKeyDown = (event: CarouselKeyboardEventType) => {
        if(event.altKey || event.ctrlKey || event.metaKey || isCarouselTabTarget({
            target: event.target
        }) || isCarouselEditableTarget({
            target: event.target
        })) {
            return;
        }

        const step = getCarouselKeyStep({
            key: event.key,
            orientation,
            direction
        });

        if(!step) {
            return;
        }

        event.preventDefault();

        if(step > 0) {
            goNext();

            return;
        }

        goPrevious();
    };

    webHandlersRef.current = {
        onPointerEnter,
        onPointerLeave,
        onKeyDown,
        onFocus,
        onBlur
    };

    const webProps: CarouselWebPropsType = {
        "aria-roledescription": localize("carousel")
    };

    const context = useMemo<CarouselContextType>(() => ({
        panHandlers: panResponder.panHandlers,
        handleCount: setCount,
        handleViewportLayout,
        isPreviousDisabled,
        isAutoplayEnabled,
        isAutoplayPlaying,
        controlsPlacement,
        handleTogglePlay,
        renderThumbnail,
        isNextDisabled,
        customLocalize,
        visibleRange,
        customTheme,
        orientation,
        activeIndex,
        isMeasured,
        goPrevious,
        direction,
        isLoading,
        indicator,
        position,
        perView,
        metrics,
        goNext,
        count,
        goTo,
        size,
        gap
    }), [
        panResponder.panHandlers,
        setCount,
        handleViewportLayout,
        isPreviousDisabled,
        isAutoplayEnabled,
        isAutoplayPlaying,
        controlsPlacement,
        handleTogglePlay,
        renderThumbnail,
        isNextDisabled,
        customLocalize,
        visibleRange,
        customTheme,
        orientation,
        activeIndex,
        isMeasured,
        goPrevious,
        direction,
        isLoading,
        indicator,
        position,
        perView,
        metrics,
        goNext,
        count,
        goTo,
        size,
        gap
    ]);

    const {
        container: containerDynamicStyle,
        viewport: viewportDynamicStyle,
        main: mainDynamicStyle,
        bar: barDynamicStyle
    } = useStyles({
        length: currentLength,
        orientation,
        spaces
    });

    const overlayStyles = getCarouselOverlayStyle({
        shapeConfigs,
        orientation,
        spaces
    });

    const cornerRadius = concentric({
        inset: spaces.spacingSm,
        outer: radiuses.surface,
        role: radiuses.actions
    });

    const renderInsideControls = () => {
        if(controlsPlacement !== "inside" || !isShowControls) {
            return null;
        }

        return [
            <View
                style={[
                    stylesheet.overlay,
                    isVertical ? stylesheet.overlayTop : stylesheet.overlayStart,
                    overlayStyles.previous
                ]}
                key="previous"
            >
                <CarouselPrevious />
            </View>,
            <View
                style={[
                    stylesheet.overlay,
                    isVertical ? stylesheet.overlayBottom : stylesheet.overlayStart,
                    overlayStyles.next
                ]}
                key="next"
            >
                <CarouselNext />
            </View>
        ];
    };

    const renderCornerControls = () => {
        return [
            controlsPlacement !== "end" && isShowControls && isAutoplayEnabled ? <View
                style={[
                    stylesheet.overlay,
                    overlayStyles.pause
                ]}
                key="pause"
            >
                <CarouselPause
                    borderRadius={cornerRadius}
                />
            </View> : null,
            indicator === "badge" && isShowControls && !isLoading ? <View
                style={[
                    stylesheet.overlay,
                    stylesheet.inert,
                    overlayStyles.badge
                ]}
                key="badge"
            >
                <CarouselIndicators
                    type="badge"
                />
            </View> : null
        ];
    };

    const renderOutsideSlot = (part: "previous" | "next") => {
        if(controlsPlacement !== "outside" || !isShowControls) {
            return null;
        }

        return <View
            style={stylesheet.outsideSlot}
        >
            {
                part === "previous" ?
                    <CarouselPrevious />
                :
                    <CarouselNext />
            }
        </View>;
    };

    const renderBar = () => {
        const isShowNav = isEndControls && !isCompactIndicators;

        if(!isEndControls && !isIndicatorInBar) {
            return null;
        }

        return <View
            style={[
                isVertical ? stylesheet.barVertical : stylesheet.bar,
                barDynamicStyle
            ]}
        >
            {
                isEndControls ?
                    <CarouselPause />
                :
                    null
            }
            {
                isIndicatorInBar ?
                    <View
                        style={[
                        isVertical ? stylesheet.growVertical : stylesheet.grow,
                        controlsPlacement === "end" ? stylesheet.growStart : undefined,
                        indicator === "dots" ? getCarouselBarStyle({
                            isEndPlacement: controlsPlacement === "end" && !isVertical,
                            touchTargets,
                            pointerType,
                            borders
                        }) : undefined
                        ]}
                    >
                        <CarouselIndicators />
                    </View>
                :
                    <View
                        style={stylesheet.grow}
                    />
            }
            {
                isShowNav ?
                    <View
                        style={isVertical ? stylesheet.navVertical : stylesheet.nav}
                    >
                        <CarouselPrevious />
                        <CarouselNext />
                    </View>
                :
                    null
            }
        </View>;
    };

    return <CarouselContext.Provider
        value={context}
    >
        <View
            {...props}
            {...webProps}
            style={[
                style,
                stylesheet.container,
                isVertical ? stylesheet.containerVertical : undefined,
                containerDynamicStyle
            ]}
            aria-label={ariaLabel}
            aria-busy={isLoading}
            ref={rootRef}
            role="region"
        >
            <View
                style={[
                    isVertical ? stylesheet.mainVertical : stylesheet.main,
                    mainDynamicStyle
                ]}
            >
                {renderOutsideSlot("previous")}
                <View
                    style={[
                        stylesheet.viewport,
                        isVertical ? stylesheet.viewportVertical : undefined,
                        viewportDynamicStyle
                    ]}
                    onLayout={handleViewportLayout}
                >
                    {children}
                    {renderInsideControls()}
                    {renderCornerControls()}
                </View>
                {renderOutsideSlot("next")}
            </View>
            {renderBar()}
        </View>
    </CarouselContext.Provider>;
};

export default Object.assign(Carousel, {
    Indicators: CarouselIndicators,
    Previous: CarouselPrevious,
    Content: CarouselContent,
    Pause: CarouselPause,
    Item: CarouselItem,
    Next: CarouselNext
});
